import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Building, MapPin, Phone, Mail, ArrowLeft, Home, Users, DollarSign, Edit, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [propRes, unitsRes, leasesRes] = await Promise.all([
        axios.get(`${API}/corporations`),
        axios.get(`${API}/units?entity_id=${id}`),
        axios.get(`${API}/leases?entity_id=${id}`)
      ]);
      const prop = propRes.data.find(p => p.id === id);
      setProperty(prop);
      setUnits(unitsRes.data);
      setLeases(leasesRes.data);
      if (prop) {
        setFormData({
          name: prop.name || '', legal_name: prop.legal_name || '',
          property_type: prop.property_type || 'residential',
          address: prop.address || '', city: prop.city || '',
          province: prop.province || 'ON', postal_code: prop.postal_code || '',
          phone: prop.phone || '', email: prop.email || '',
          business_number: prop.business_number || '', hst_number: prop.hst_number || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/corporations/${id}`, formData);
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to update property:', error);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Property not found</p>
        <Link to="/properties" className="text-indigo-600 hover:underline mt-2 inline-block">Back to Properties</Link>
      </div>
    );
  }

  const occupiedUnits = units.filter(u => u.status === 'occupied').length;
  const activeLeases = leases.filter(l => l.status === 'active').length;
  const totalMonthlyRevenue = leases.filter(l => l.status === 'active').reduce((sum, l) => sum + (l.total_monthly || 0), 0);

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/properties" className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{property.name}</h1>
            <p className="text-slate-500">{property.legal_name}</p>
          </div>
        </div>
        <button onClick={() => setShowEditModal(true)} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
          <Edit className="w-4 h-4" /> Edit Property
        </button>
      </div>

      {/* Property Info Card */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-6">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-xl">
              <Building className="w-8 h-8 text-white" />
            </div>
            <div>
              <span className="text-xs px-2 py-1 rounded-full bg-white/20 text-white capitalize">{property.property_type}</span>
              <p className="text-white/80 mt-1 text-sm">{property.address}, {property.city}, {property.province} {property.postal_code}</p>
            </div>
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 p-2 rounded-lg"><Phone className="w-5 h-5 text-slate-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Phone</p>
              <p className="font-medium text-slate-900">{property.phone || 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 p-2 rounded-lg"><Mail className="w-5 h-5 text-slate-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Email</p>
              <p className="font-medium text-slate-900">{property.email || 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 p-2 rounded-lg"><Building className="w-5 h-5 text-slate-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Business Number</p>
              <p className="font-medium text-slate-900">{property.business_number || 'Not set'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 p-2 rounded-lg"><Home className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{units.length}</p>
              <p className="text-sm text-slate-500">Total Units</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg"><Users className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{occupiedUnits}</p>
              <p className="text-sm text-slate-500">Occupied</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-violet-100 p-2 rounded-lg"><Users className="w-5 h-5 text-violet-600" /></div>
            <div>
              <p className="text-2xl font-bold text-violet-600">{activeLeases}</p>
              <p className="text-sm text-slate-500">Active Leases</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-lg"><DollarSign className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalMonthlyRevenue)}</p>
              <p className="text-sm text-slate-500">Monthly Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Units List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Units</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Details</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Monthly</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {units.map((unit) => (
                <tr key={unit.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{unit.unit_number}</p>
                    <p className="text-xs text-slate-500">Floor {unit.floor}</p>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 capitalize">{unit.type}</span></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{unit.bedrooms > 0 && `${unit.bedrooms} bed • `}{unit.bathrooms} bath • {unit.square_feet} sqft</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{formatCurrency(unit.monthly_rent || unit.monthly_maintenance_fee)}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${unit.status === 'occupied' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{unit.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Edit Property</h3>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Legal Name</label>
                  <input type="text" value={formData.legal_name} onChange={(e) => setFormData({...formData, legal_name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select value={formData.property_type} onChange={(e) => setFormData({...formData, property_type: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="residential">Residential</option>
                    <option value="condo">Condo</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">City</label><input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Province</label><input type="text" value={formData.province} onChange={(e) => setFormData({...formData, province: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Postal Code</label><input type="text" value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
